import { httpsCallable } from '../firebase/app'
import { isNativePlatform, getPlatform } from './runtime'
import { getOrCreateDeviceId, clearDeviceId } from './adapters/deviceIdentity'

/**
 * Client-side service for #1691's three NATIVE_DEVICE contracts (spec.md
 * §10.2). Every function here is a no-op on web — this entity and its
 * callables exist only for the native wrapper (#467) — so callers do not
 * need their own `isNativePlatform()` guard before using this module.
 *
 * `appVersion`/`buildNumber` come from the shell itself (#2410): when the
 * caller does not pass them, `registerThisDevice` reads them from Capacitor's
 * `@capacitor/app` `getInfo()` — the same `window.Capacitor.Plugins.App`
 * lookup `deepLink/nativeLinkListener.js` uses, so this file still carries no
 * import that could fail to resolve. Only when the plugin is absent or its
 * call fails (a device build synced before the plugin, a rejected call) does
 * `appVersion` fall back to `NATIVE_APP_VERSION_UNKNOWN` — an honest "we
 * don't know" value, not a fabricated one. A forced-update check must treat
 * that value as "cannot compare", never as a real version.
 */
export const NATIVE_APP_VERSION_UNKNOWN = 'unknown'

function getAppPlugin() {
  try {
    return (typeof window !== 'undefined' && window.Capacitor?.Plugins?.App) || null
  } catch {
    return null
  }
}

/**
 * Reads the installed app's version/build from the native shell.
 *
 * @return {Promise<{appVersion: string, buildNumber: ?string}>} `appVersion`
 *   is NATIVE_APP_VERSION_UNKNOWN (and `buildNumber` null) whenever the shell
 *   cannot say — never throws.
 */
export async function readNativeAppInfo() {
  const App = getAppPlugin()
  if (!App || typeof App.getInfo !== 'function') {
    return { appVersion: NATIVE_APP_VERSION_UNKNOWN, buildNumber: null }
  }
  try {
    const info = await App.getInfo()
    const version = typeof info?.version === 'string' ? info.version.trim() : ''
    if (!version) {
      return { appVersion: NATIVE_APP_VERSION_UNKNOWN, buildNumber: null }
    }
    const build = info?.build
    return {
      appVersion: version,
      buildNumber: build === undefined || build === null || build === '' ? null : String(build),
    }
  } catch {
    return { appVersion: NATIVE_APP_VERSION_UNKNOWN, buildNumber: null }
  }
}

/**
 * Registers (or heartbeats/updates) this install's own NATIVE_DEVICE doc.
 * Called once after a fresh native OTP login succeeds, and again whenever
 * the employee explicitly asks this device to become the Primary Device for
 * background-location challenges (§5.4) — see that flag's own doc comment
 * in `functions/nativeApp/registerNativeDevice.js` for why the swap is
 * folded into this same call rather than a separate one.
 *
 * @param {object} [params] Parameters.
 * @param {string} [params.appVersion] Defaults to the shell's real version
 *   (`readNativeAppInfo`), or NATIVE_APP_VERSION_UNKNOWN when the shell can't
 *   say — see this file's header.
 * @param {?string} [params.buildNumber] Defaults to the shell's build number.
 * @param {?string} [params.osVersion]
 * @param {string[]} [params.capabilities]
 * @param {boolean} [params.requestPrimaryForBackgroundLocation]
 * @return {Promise<?object>} null on web.
 */
export async function registerThisDevice({
  appVersion,
  buildNumber,
  osVersion = null,
  capabilities = [],
  requestPrimaryForBackgroundLocation = false,
} = {}) {
  if (!isNativePlatform()) {
    return null
  }
  let resolvedVersion = appVersion
  let resolvedBuild = buildNumber
  if (resolvedVersion === undefined || resolvedBuild === undefined) {
    const info = await readNativeAppInfo()
    if (resolvedVersion === undefined) resolvedVersion = info.appVersion
    if (resolvedBuild === undefined) resolvedBuild = info.buildNumber
  }
  const deviceId = await getOrCreateDeviceId()
  const result = await httpsCallable('registerNativeDevice')({
    deviceId,
    platform: getPlatform(),
    appVersion: resolvedVersion,
    buildNumber: resolvedBuild,
    osVersion,
    capabilities,
    requestPrimaryForBackgroundLocation,
  })
  return result?.data ?? null
}

/**
 * Submits a fresh APNs/FCM installation token for this device (#1695's own
 * job is to actually obtain that token; this just stores it).
 *
 * @param {string} pushToken
 * @return {Promise<?object>} null on web.
 */
export async function refreshThisDevicePushToken(pushToken) {
  if (!isNativePlatform()) {
    return null
  }
  const deviceId = await getOrCreateDeviceId()
  const result = await httpsCallable('refreshNativePushToken')({ deviceId, pushToken })
  return result?.data ?? null
}

/**
 * Self-revoke — what native logout maps to (§5.2): invalidates the device
 * server-side and clears the locally-persisted device id, so a later login
 * on this same install mints a fresh NATIVE_DEVICE identity rather than
 * trying to reuse the now-terminal one (see `nativeDeviceModel.js`'s
 * `isTerminalDeviceStatus`).
 *
 * @param {?string} [reason]
 * @return {Promise<?object>} null on web.
 */
export async function revokeThisDevice(reason = 'user_logout') {
  if (!isNativePlatform()) {
    return null
  }
  const deviceId = await getOrCreateDeviceId()
  const result = await httpsCallable('revokeNativeDevice')({ deviceId, reason })
  await clearDeviceId()
  return result?.data ?? null
}

/**
 * Employer/admin security revoke of ANY employee's device — never callable
 * for a device link, only ever for revoke (§18): there is no
 * `registerDeviceForUser`-shaped counterpart anywhere in this module or in
 * `functions/nativeApp/registerNativeDevice.js`, by construction.
 *
 * @param {string} deviceId
 * @param {?string} [reason]
 * @return {Promise<object>}
 */
export async function revokeEmployeeDevice(deviceId, reason = null) {
  const result = await httpsCallable('revokeNativeDevice')({ deviceId, reason })
  return result?.data ?? {}
}
